import { useMemo } from 'react'
import { useQuery } from '@apollo/client/react'
import type { LucideIcon } from 'lucide-react'
import { PortalPackageCatalogDocument } from '@/graphql/generated/graphql'
import {
  PACKAGE_TYPE_LABELS,
  PACKAGE_TYPE_ORDER,
  iconForType,
  normalizePackageType,
  type PackageType,
} from '../constants/serviceCatalogMeta'

export interface CatalogService {
  /** packageServiceId numérico (el `id` del catálogo viene como string/ID). */
  id: number
  name: string
  description: string
  code: string
  /** Tipo del paquete (Zeus): PLAN | MODULE | SERVICE. */
  type: PackageType
  /** Etiqueta del tipo en español (Planes / Módulos / Servicios). */
  typeLabel: string
  icon: LucideIcon
  /** Precio mensual del backend. */
  monthlyPrice: number
  /** Precio anual = mensual × 12. */
  annualPrice: number
  currencyId: number | null
  currencyName: string
  /** false → backend no devolvió precio (mostrar "a confirmar"). */
  hasPrice: boolean
}

export interface CatalogGroup {
  type: PackageType
  /** Etiqueta en español del grupo (Planes / Módulos / Servicios). */
  label: string
  services: CatalogService[]
}

/** Forma mínima del item de `portalPackageCatalog` que consumimos. */
export interface RawCatalogItem {
  id: string | number
  name: string
  description: string
  code: string
  price?: number | null
  currencyId?: number | null
  type?: string | null
  currency?: { name?: string | null } | null
}

/**
 * Mapea el catálogo crudo del backend a `CatalogService`. Solo el ícono se
 * resuelve localmente; precio, tipo y moneda vienen del backend. `hasPrice` es
 * `false` cuando el backend no trae precio o el precio es 0 (no contratable).
 */
export function mapCatalogItems(items: readonly RawCatalogItem[]): CatalogService[] {
  return items.map((item) => {
    const monthlyPrice = item.price ?? 0
    const type = normalizePackageType(item.type)
    return {
      id: Number(item.id),
      name: item.name,
      description: item.description,
      code: item.code,
      type,
      typeLabel: PACKAGE_TYPE_LABELS[type],
      icon: iconForType(type),
      monthlyPrice,
      annualPrice: monthlyPrice * 12,
      currencyId: item.currencyId ?? null,
      currencyName: item.currency?.name ?? '',
      hasPrice: item.price != null && item.price > 0,
    }
  })
}

/**
 * Agrupa el catálogo por TIPO de paquete (Planes / Módulos / Servicios), en el
 * orden fijo `PACKAGE_TYPE_ORDER`. Solo aparecen los tipos que tienen servicios.
 */
export function groupByType(services: readonly CatalogService[]): CatalogGroup[] {
  const byType = new Map<PackageType, CatalogService[]>()
  for (const s of services) {
    const list = byType.get(s.type)
    if (list) list.push(s)
    else byType.set(s.type, [s])
  }

  return PACKAGE_TYPE_ORDER.filter((t) => byType.has(t)).map((type) => ({
    type,
    label: PACKAGE_TYPE_LABELS[type],
    services: byType.get(type) ?? [],
  }))
}

/**
 * Catálogo público de servicios. Usa precio/tipo/moneda reales del backend y
 * agrupa por el tipo de cada servicio. Los servicios sin precio (o precio 0) NO
 * se ofrecen (no son contratables), pero sí quedan en `byId` para resolver
 * nombres/precios de servicios ya seleccionados o contratados.
 */
export function usePackageCatalog() {
  const { data, loading, error, refetch } = useQuery(PortalPackageCatalogDocument, {
    fetchPolicy: 'cache-first',
    notifyOnNetworkStatusChange: true,
  })

  // Catálogo crudo del backend (incluye no contratables), para resolución por id.
  const allServices = useMemo<CatalogService[]>(
    () => mapCatalogItems(data?.portalPackageCatalog ?? []),
    [data],
  )

  // Servicios OFRECIBLES: solo los que tienen precio (> 0). Un servicio sin
  // precio o con precio 0 no debe mostrarse como disponible para elegir (ni en
  // el catálogo público ni en los wizards).
  const services = useMemo<CatalogService[]>(
    () => allServices.filter((s) => s.hasPrice),
    [allServices],
  )

  const byId = useMemo(() => {
    const map = new Map<number, CatalogService>()
    for (const s of allServices) map.set(s.id, s)
    return map
  }, [allServices])

  const currencyNameById = useMemo(() => {
    const map = new Map<number, string>()
    for (const s of allServices) {
      if (s.currencyId != null && s.currencyName) map.set(s.currencyId, s.currencyName)
    }
    return map
  }, [allServices])

  const grouped = useMemo<CatalogGroup[]>(() => groupByType(services), [services])

  return { services, byId, grouped, currencyNameById, loading, error, refetch }
}
